import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import LoginRedirect from "@/components/LoginRedirect";

// Pages
import LandingPage from "@/pages/LandingPage";
import LoginPage from "@/pages/LoginPage";
import SignupPage from "@/pages/SignupPage";
import ForgotPasswordPage from "@/pages/ForgotPasswordPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import AdminLoginPage from "@/pages/AdminLoginPage";
import ApplicantDashboard from "@/pages/ApplicantDashboard";
import ApplicationForm from "@/pages/ApplicationForm";
import ApplicationPreview from "@/pages/ApplicationPreview";
import SponsorDashboard from "@/pages/SponsorDashboard";
import ReviewDashboard from "@/pages/ReviewDashboard";
import AdminDashboard from "@/pages/AdminDashboard";
import AcceptInvite from "@/pages/AcceptInvite";
import EndorsementPage from "@/pages/EndorsementPage";
import CommitteeJoinPage from "@/pages/CommitteeJoinPage";
import CommitteeDashboard from "@/pages/CommitteeDashboard";
import CommitteeLandingPage from "@/pages/CommitteeLandingPage";
import MyCommitteesPage from "@/pages/MyCommitteesPage";
import AccessRequestPage from "@/pages/AccessRequestPage";

// Protected Route Component
const ProtectedRoute = ({ children, allowedRoles }) => {
  const { user, loading } = useAuth();
  const location = useLocation();
  const pathSegments = location.pathname.split('/').filter(Boolean);
  const committeeSlug = pathSegments.length > 1 && ['dashboard', 'application'].includes(pathSegments[1])
    ? pathSegments[0]
    : null;
  const loginTarget = committeeSlug
    ? `/login?committee=${committeeSlug}&redirect=${location.pathname}`
    : '/login';
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-greek-blue border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-600">Loading...</p>
        </div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to={loginTarget} replace />;
  }
  
  if (allowedRoles && !allowedRoles.includes(user.role)) {
    // Redirect to appropriate dashboard based on role
    const dashboardRoutes = {
      applicant: '/dashboard',
      sponsor: '/sponsor',
      chair: '/my-committees',
      co_chair: '/my-committees',
      committee: '/my-committees',
      admin: '/admin'
    };
    return <Navigate to={dashboardRoutes[user.role] || '/login'} replace />;
  }
  
  return children;
};

function AppRoutes() {
  const { user } = useAuth();
  
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={user ? <Navigate to={
        user.role === 'admin' ? '/admin' :
        user.role === 'sponsor' ? '/sponsor' :
        ['chair', 'co_chair', 'committee'].includes(user.role) ? '/my-committees' :
        '/dashboard'
      } replace /> : <LandingPage />} />
      <Route path="/login" element={user ? (
        <LoginRedirect user={user} />
      ) : <LoginPage />} />
      <Route path="/signup" element={user ? <Navigate to="/dashboard" replace /> : <SignupPage />} />
      <Route path="/forgot-password" element={user ? <Navigate to="/dashboard" replace /> : <ForgotPasswordPage />} />
      <Route path="/reset-password/:token" element={<ResetPasswordPage />} />
      <Route path="/admin/login" element={user?.role === 'admin' ? <Navigate to="/admin" replace /> : <AdminLoginPage />} />
      <Route path="/invite/:token" element={<AcceptInvite />} />
      <Route path="/endorse/:token" element={<EndorsementPage />} />
      
      {/* Committee Landing Pages */}
      <Route path="/:committeeSlug" element={<CommitteeLandingPage />} />
      
      {/* Committee Routes (Public with token) */}
      <Route path="/committee/join/:token" element={<CommitteeJoinPage />} />
      <Route path="/access-request/:token" element={<AccessRequestPage />} />
      
      {/* Applicant Routes */}
      <Route path="/:committeeSlug/dashboard" element={
        <ProtectedRoute allowedRoles={['applicant']}>
          <ApplicantDashboard />
        </ProtectedRoute>
      } />
      <Route path="/:committeeSlug/application/:scholarshipId" element={
        <ProtectedRoute allowedRoles={['applicant']}>
          <ApplicationForm />
        </ProtectedRoute>
      } />
      <Route path="/:committeeSlug/application/:scholarshipId/:applicationId" element={
        <ProtectedRoute allowedRoles={['applicant']}>
          <ApplicationForm />
        </ProtectedRoute>
      } />
      <Route path="/dashboard" element={
        <ProtectedRoute allowedRoles={['applicant']}>
          <ApplicantDashboard />
        </ProtectedRoute>
      } />
      <Route path="/application/:scholarshipId" element={
        <ProtectedRoute allowedRoles={['applicant']}>
          <ApplicationForm />
        </ProtectedRoute>
      } />
      <Route path="/application/:scholarshipId/:applicationId" element={
        <ProtectedRoute allowedRoles={['applicant']}>
          <ApplicationForm />
        </ProtectedRoute>
      } />
      
      {/* Sponsor Routes */}
      <Route path="/sponsor" element={
        <ProtectedRoute allowedRoles={['sponsor']}>
          <SponsorDashboard />
        </ProtectedRoute>
      } />
      
      {/* My Committees Overview Page */}
      <Route path="/my-committees" element={
        <ProtectedRoute allowedRoles={['chair', 'co_chair', 'committee', 'admin']}>
          <MyCommitteesPage />
        </ProtectedRoute>
      } />
      
      {/* Committee Dashboard (Chair, Co-Chair, Committee Members & Admin) */}
      <Route path="/committee-dashboard" element={
        <ProtectedRoute allowedRoles={['chair', 'co_chair', 'committee', 'admin']}>
          <CommitteeDashboard />
        </ProtectedRoute>
      } />
      
      {/* Review Routes (Chair & Committee) - Legacy route */}
      <Route path="/review" element={
        <ProtectedRoute allowedRoles={['chair', 'co_chair', 'committee']}>
          <ReviewDashboard />
        </ProtectedRoute>
      } />
      
      {/* Admin Routes */}
      <Route path="/admin" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <AdminDashboard />
        </ProtectedRoute>
      } />
      
      {/* Application Preview (Admin and Committee Members) */}
      <Route path="/preview/:scholarshipId" element={
        <ProtectedRoute allowedRoles={['admin', 'chair', 'co_chair', 'committee']}>
          <ApplicationPreview />
        </ProtectedRoute>
      } />
      <Route path="/application/preview/:scholarshipId" element={
        <ProtectedRoute allowedRoles={['admin', 'chair', 'co_chair', 'committee']}>
          <ApplicationPreview />
        </ProtectedRoute>
      } />
      
      {/* Catch all */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
